import { resultsOfGainingCard } from '@mythos/game/AIUtilities';
import { ActionTypes, GameAction } from '@mythos/game/GameActions';
import { GamePlayerViewModel, GameViewModel } from '@mythos/game/GameViewModel';
import { Phase } from '@mythos/game/Phases';
import * as Rules from '@mythos/game/Rules';
import {
  getTutorialData,
  TutorialHighlightElementCardAnatomy,
  TutorialHighlightType,
  TutorialTooltip,
  TutorialTurnData,
} from '@mythos/game/Tutorial';
import { clamp } from '@mythos/utils/utils';
import invariant from 'invariant';
import { without } from 'lodash';
import nullthrows from 'nullthrows';
import { useEffect, useReducer } from 'react';
import { match } from 'ts-pattern';
import GameMutator from './GameMutator';

type ActionStoreListener = () => void;

export type ActionStoreCanSubmitResult = {
  result: boolean;
  message?: string;
};
export default class ActionStore {
  private _userID: string;
  private _game: GameViewModel;
  private _listeners: ActionStoreListener[] = [];

  private _selectedCard: Rules.CardWithID | null = null;
  private _military: number = 0;

  // tutorial state
  private _tutorialMessageIndex: number = 0;

  constructor(game: GameViewModel, userID: string) {
    this._userID = userID;
    this._game = game;
    console.log('action store constructor game', game);

    this._military = this.getMilitaryBidLimits().min;

    this.clearSelection();
    this.updateSelectionsFromGame_();
  }

  addListener(callback: ActionStoreListener) {
    this._listeners.push(callback);
  }
  removeListener(callback: ActionStoreListener) {
    this._listeners = without(this._listeners, callback);
  }

  setGameIfNecessary(game: GameViewModel) {
    if (!game) {
      return;
    }
    if (!this._game || this._game.sequenceID < game.sequenceID) {
      this.setGame(game);
    }
  }

  setGame(game: GameViewModel) {
    const old_phase = this._game.phase;
    this._game = game;

    console.log('action store set game', game.phase);
    if (game.phase === Phase.WAR) {
      this.updateSelectionsFromGame_();
    }

    if (old_phase != game.phase) {
      this._tutorialMessageIndex = 0;

      const selectedCardID = this.getPlayer().selectedCard?.id;
      if (game.phase === Phase.PLANNING) {
        this._selectedCard = null;
        this._military = this.getMilitaryBidLimits().min;
      } else if (game.phase === Phase.RESOLUTION && selectedCardID) {
        this.updateSelectionsFromGame_();
        var selected_card = this._game.table.find(
          (card) => card.id === selectedCardID,
        );
        if (!selected_card) {
          this.clearSelection();
          return;
        }
        this._selectedCard = selected_card;
      } else {
        this.clearSelection();
      }
    } else if (this._selectedCard) {
      this._selectedCard = nullthrows(
        this._game.cardsByID[this._selectedCard.id],
      ) as Rules.CardWithID;
    }

    this.onSelectionsChanged_();
  }

  updateSelectionsFromGame_() {
    let game = this._game;
    let player = this.getPlayer();

    const bid = this._game.bidsByPlayerID[this.getPlayer().userID];
    if (bid) {
      this._selectedCard = this._game.table[bid.tradeRowIndex];
      this._military = bid.military;
    }

    if (player.bid) {
      this._military = player.bid.military;
      this._selectedCard = game.table[
        player.bid.tradeRowIndex
      ] as Rules.CardWithID;
    }

    const resolve = this._game.resolvesByPlayerID[this.getPlayer().userID];
    if (resolve) {
      this._selectedCard = this._game.cardsByID[resolve.cardIDToGain];
    }
  }

  clearSelection() {
    this._selectedCard = null;
  }

  getSelectedCardID(): string | null {
    return this._selectedCard ? this._selectedCard.id : null;
  }
  getSelectedCard(): Rules.CardWithID | null {
    return this._selectedCard;
  }

  getMilitaryBidOrNan(): number {
    return this._military;
  }
  getMilitaryBid(): number {
    return this._military || 0;
  }
  getMilitaryBidLimits(): { min: number; max: number } {
    const player = this.getPlayer();
    const baseCounters = Rules.computeBaseCounters(this.getContext());
    return {
      min: baseCounters.military,
      max: player.counters.military + baseCounters.military,
    };
  }

  canUndo(): boolean {
    return (
      this._game.options.unbid &&
      this._game.phase === Phase.PLANNING &&
      this._game.readyByUserID[this.getPlayer().userID]
    );
  }

  shouldShowTutorialUserHighlights(): boolean {
    const tutorialMessages = this._getTutorialMessages();
    return (
      tutorialMessages &&
      this._tutorialMessageIndex >= tutorialMessages.length - 1
    );
  }

  private _getTutorialMessages(): TutorialTooltip[] {
    const tutorialData = this.getTutorialData();
    if (!tutorialData) {
      return [];
    }
    return (
      match(this._game.phase)
        .with(Phase.PLANNING, () => tutorialData.planningMessages)
        .with(Phase.RESOLUTION, () => tutorialData.resolutionMessages)
        .otherwise(() => []) || []
    );
  }
  private _isOnTutorialLastMessage(): boolean {
    const messages = this._getTutorialMessages();
    return this._tutorialMessageIndex >= messages.length - 1;
  }
  private _isTutorialActionFulfilled(): boolean {
    const tutorialData = this.getTutorialData();
    const expectedAction =
      this._game.phase === Phase.PLANNING
        ? tutorialData?.playerPlanningAction
        : tutorialData?.playerResolutionAction;

    if (!expectedAction) return true; // No enforced action, considered fulfilled

    // Check if current selection matches expected
    const expectedDefID = match(expectedAction.cardSelection)
      .with({ type: 'trade_row_card' }, ({ defID }) => defID)
      .with({ type: 'basic_card' }, ({ defID }) => defID)
      .exhaustive();

    if (this._selectedCard?.defID !== expectedDefID) return false;

    // For planning, also check military bid if specified
    if (
      expectedAction.phase === Phase.PLANNING &&
      expectedAction.militaryBid !== undefined
    ) {
      if (this.getMilitaryBid() !== expectedAction.militaryBid) return false;
    }

    return true;
  }

  canSubmit(): ActionStoreCanSubmitResult {
    var player = this.getPlayer();
    try {
      if (this._game.options.tutorial) {
        const tutorialData = this.getTutorialData();
        if (tutorialData) {
          const tutorialMessages = this._getTutorialMessages();
          if (
            tutorialMessages &&
            this._tutorialMessageIndex < tutorialMessages.length
          ) {
            // On the last message, allow submission if the enforced action is fulfilled
            const canSubmitOnLastMessage = this._isOnTutorialLastMessage();
            if (!canSubmitOnLastMessage) {
              return {
                result: false,
                message: 'Please read all tutorial messages before proceeding.',
              };
            }
          }
          const action =
            this._game.phase === Phase.PLANNING
              ? tutorialData.playerPlanningAction
              : tutorialData.playerResolutionAction;
          if (action) {
            const cardDefID = match(action.cardSelection)
              .with({ type: 'trade_row_card' }, ({ defID }) => defID)
              .with({ type: 'basic_card' }, ({ defID }) => defID)
              .exhaustive();

            if (this._selectedCard?.defID !== cardDefID) {
              const cardName =
                Object.values(this._game.cardsByID).find(
                  (c) => c.defID === cardDefID,
                )?.name ?? cardDefID;
              return {
                result: false,
                message: `Please select ${cardName}`,
              };
            }
            if (
              action.phase === Phase.PLANNING &&
              action.militaryBid !== undefined &&
              this.getMilitaryBid() !== action.militaryBid
            ) {
              return {
                result: false,
                message: `Please bid ${action.militaryBid} military`,
              };
            }
          }
        }
      }

      if (this._game.phase === Phase.PLANNING) {
        if (!this._selectedCard) {
          return {
            result: false,
            message: 'You must select a card to draft',
          };
        }
        const tradeRowIndex = this._game.table.findIndex(
          (x) => x.id === this._selectedCard!.id,
        );
        Rules.validateBid(
          player,
          { military: this.getMilitaryBid(), tradeRowIndex },
          this._game,
        );
      } else if (this._game.phase === Phase.RESOLUTION) {
        if (!this._selectedCard) {
          return {
            result: false,
            message: 'You must select a card to gain',
          };
        }
        Rules.validateResolve(
          player,
          { cardIDToGain: this._selectedCard!.id },
          this._game,
        );
      }

      return {
        result: true,
      };
    } catch (e: any) {
      return {
        result: false,
        message: e.message,
      };
    }
  }

  getUnaffordableCards(): Map<string, string> {
    let cards: Rules.CardWithID[] = [];
    if (this._game.phase === Phase.PLANNING) {
      cards = [...this._game.table, ...this._game.basicPile];
    } else if (this._game.phase === Phase.RESOLUTION) {
      cards = [...this._game.basicPile];
      const selectedCard = this.getPlayer().selectedCard;
      if (selectedCard) {
        cards.push(selectedCard);
      }
    }

    const ret = new Map<string, string>();
    const context = this.getContext();
    cards.forEach((card) => {
      const canAfford = Rules.canAffordCard(context, card);
      if (!canAfford.canAfford) {
        ret.set(card.id, canAfford.reason);
      }
    });
    return ret;
  }

  getCompletedTributeIDs(): string[] {
    // XXX: some tributes might not require a selection to be completed
    // this will prevent them from being optimistically highlighted before
    // a card is selected
    if (!this._selectedCard) {
      return [];
    }

    return resultsOfGainingCard(
      this._game,
      this.getPlayer(),
      this._selectedCard,
      this.getMilitaryBid(),
    ).completedTributeIDs;
  }
  getDisallowedCardIDs(): Set<string> {
    if (this._game.phase === Phase.RESOLUTION) {
      let disallowed = new Set(this.getUnaffordableCards().keys());

      this._game.table.forEach((card) => {
        if (this.getPlayer().selectedCard?.id === card.id) {
          return;
        }
        disallowed.add(card.id);
      });
      return disallowed;
    }
    return new Set();
  }
  canAffordCard(card: Rules.CardWithID): Rules.CanAffordCardResult {
    return Rules.canAffordCard(this.getContext(), card);
  }
  getTutorialData(): TutorialTurnData | null {
    if (this._game.options.tutorial) {
      return getTutorialData(this._game.age, this._game.turn);
    }
    return null;
  }
  getTutorialMessage(): TutorialTooltip | null {
    const tutorialData = this.getTutorialData();
    if (!tutorialData) {
      return null;
    }
    return match(this._game.phase)
      .with(
        Phase.PLANNING,
        () =>
          tutorialData.planningMessages?.[this._tutorialMessageIndex] ?? null,
      )
      .with(
        Phase.RESOLUTION,
        () =>
          tutorialData.resolutionMessages?.[this._tutorialMessageIndex] ?? null,
      )
      .otherwise(() => null);
  }
  /**
   * Returns true if the Next button should be enabled for the current tutorial message.
   * On the last message with an enforced action, always returns false so the user
   * must click Ready to proceed (not Next).
   */
  isTutorialNextButtonEnabled(): boolean {
    if (!this._isOnTutorialLastMessage()) return true;

    const tutorialData = this.getTutorialData();
    const expectedAction =
      this._game.phase === Phase.PLANNING
        ? tutorialData?.playerPlanningAction
        : tutorialData?.playerResolutionAction;

    // On last message with enforced action, always disable Next - user must click Ready
    if (expectedAction) return false;

    return true;
  }
  getTutorialHighlightCard(): {
    card: Rules.CardWithID;
    highlights: TutorialHighlightElementCardAnatomy[];
  } | null {
    const message = this.getTutorialMessage();
    if (message?.highlightType !== TutorialHighlightType.CARD_ANATOMY) {
      return null;
    }
    const card = Object.values(this._game.cardsByID).find(
      (card) => card.defID === message.cardDefID,
    );
    if (!card) {
      return null;
    }
    return { card, highlights: message.highlightedElements };
  }
  getTutorialHighlightTribute(): {
    tribute: Rules.TributeCardWithID;
  } | null {
    const message = this.getTutorialMessage();
    if (message?.highlightType !== TutorialHighlightType.TRIBUTE_ANATOMY) {
      return null;
    }
    const tribute = Object.values(this._game.tributeCardsByID).find(
      (tribute) => tribute.defID === message.tributeDefID,
    );
    if (!tribute) {
      return null;
    }

    return { tribute };
  }
  getTutorialHighlightCardIDs(): Set<string> {
    if (!this.shouldShowTutorialUserHighlights()) {
      return new Set();
    }
    const ret = new Set<string>();
    const tutorialData = this.getTutorialData();
    if (!tutorialData) {
      return ret;
    }

    const card = match(this._game.phase)
      .with(Phase.PLANNING, () =>
        match(tutorialData.playerPlanningAction?.cardSelection)
          .with({ type: 'trade_row_card' }, ({ defID }) =>
            this._game.table.find((card) => card.defID === defID),
          )
          .with({ type: 'basic_card' }, ({ defID }) =>
            invariant(
              false,
              'invalid tutorial data: basic card not allowed in planning phase',
            ),
          )
          .otherwise(() => null),
      )
      .with(Phase.RESOLUTION, () =>
        match(tutorialData.playerResolutionAction?.cardSelection)
          .with({ type: 'trade_row_card' }, ({ defID }) =>
            this._game.table.find((card) => card.defID === defID),
          )
          .with({ type: 'basic_card' }, ({ defID }) =>
            this._game.basicPile.find((card) => card.defID === defID),
          )
          .otherwise(() => null),
      )
      .otherwise(() => {});

    if (card) {
      ret.add(card.id);
    }
    return ret;
  }

  getSubmitWarning(): string | null {
    if (this._game.phase === Phase.PLANNING) {
      const gainCard = this._selectedCard;
      if (!gainCard) {
        return 'You must select a card to draft';
      }

      const canAfford = this.canAffordCard(gainCard);
      if (!canAfford.canAfford) {
        const canAffordAny = this._game.table.some(
          (card) => this.canAffordCard(card).canAfford,
        );
        if (canAffordAny) {
          return canAfford.reason;
        }
      }
    }

    return null;
  }
  didClickSubmit(): void {
    if (!this.canSubmit().result) {
      return;
    }

    let warning = this.getSubmitWarning();
    if (warning) {
      if (
        !confirm(
          `${warning}. Press OK to confirm your selection, or Cancel to choose another card.`,
        )
      ) {
        return;
      }
    }

    const phase = this._game.phase;
    if (phase === Phase.PLANNING) {
      this.sendAction_({
        type: ActionTypes.BID,
        bid: {
          military: this.getMilitaryBid(),
          tradeRowIndex: this._game.table.findIndex(
            (x) => x.id === this._selectedCard!.id,
          ),
        },
      });
    } else if (phase === Phase.RESOLUTION) {
      this.sendAction_({
        type: ActionTypes.RESOLVE,
        resolve: {
          cardIDToGain: this._selectedCard!.id,
        },
      });
    } else {
      console.error('unexpected phase', phase);
    }
  }

  didClickUnbid(): void {
    invariant(this._game.phase === Phase.PLANNING, 'must be at Planning phase');
    invariant(
      !!this._game.bidsByPlayerID[this.getPlayer().userID],
      'must have bid to unbid',
    );
    this.sendAction_({
      type: ActionTypes.UNBID,
    });
  }

  didClickTutorialNext(): void {
    this._tutorialMessageIndex++;
    this.notifyListeners_();
  }

  canClickTutorialBack(): boolean {
    return this._tutorialMessageIndex > 0;
  }

  didClickTutorialBack(): void {
    this._tutorialMessageIndex--;
    this.notifyListeners_();
  }

  didChangeMilitary(military: number): void {
    if (this.isSessionPlayerReady()) {
      return;
    }
    if (this._game.phase !== Phase.PLANNING) {
      return;
    }
    const bidLimits = this.getMilitaryBidLimits();
    this._military = clamp(military, 0, bidLimits.max);

    this.notifyListeners_();
  }
  didClickDraftRowCard(card: Rules.CardWithID): void {
    if (this.isSessionPlayerReady()) {
      return;
    }
    var phase = this._game.phase;
    if (phase === Phase.PLANNING) {
      this.selectPlanningCard_(card);
    } else if (phase === Phase.RESOLUTION) {
      this.selectResolutionCard_(card);
    } else {
      console.log('no selections for this phase');
    }
  }
  didClickBasicCard(card: Rules.CardWithID): void {
    if (this.isSessionPlayerReady()) {
      return;
    }
    var phase = this._game.phase;
    if (phase === Phase.RESOLUTION) {
      this.selectResolutionCard_(card);
    }
  }

  getPlayer(): GamePlayerViewModel {
    return nullthrows(
      this._game.playerStates.find((player) => player.userID === this._userID),
    );
  }
  getContext(): Rules.Context {
    return Rules.makeContextFromGame(this.getPlayer(), this._game);
  }

  isSessionPlayerReady(): boolean {
    return this._game.readyByUserID[this.getPlayer().userID];
  }

  // Implementation Details
  notifyListeners_(): void {
    this._listeners.forEach((l) => l());
  }
  onSelectionsChanged_(): void {
    this.notifyListeners_();
  }
  selectPlanningCard_(card: Rules.CardWithID): void {
    this._selectedCard = card;
    this.onSelectionsChanged_();
  }
  selectResolutionCard_(card: Rules.CardWithID): void {
    var player = this.getPlayer();

    if (!this.canAffordCard(card).canAfford) {
      return;
    }

    const isSelection = card.id === player.selectedCard?.id;
    const isBasic = this._game.basicPile.some((basic) => basic.id === card.id);
    if (!(isSelection || isBasic)) {
      return;
    }

    this._selectedCard = card;
    this.onSelectionsChanged_();
  }
  async sendAction_(action: GameAction) {
    if (!this._game) {
      console.log('no game to send action!');
      return;
    }
    await GameMutator.sendAction(this._game.id, action);
  }
}

export function useActionStoreListener(actionStore: ActionStore) {
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    actionStore.addListener(forceUpdate);
    return () => {
      actionStore.removeListener(forceUpdate);
    };
  }, [actionStore]);
}
